/* eslint-disable camelcase */
import { useBoolean, useMount, useRequest, useUpdateEffect } from "ahooks";
import { Form, message, Modal } from "antd";
import { Moment } from "moment";
import { isNil, map, omit } from "ramda";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { postCreateTutorialApi, putEditTutorialApi } from "@/services/api";
import { getPodcastPermissionApi } from "@/services/api/ai-podcast";
import {
  ICreateTutorialRequest,
  IDeptTutorialDetail,
  IEditTutorialRequest,
  TutorialTypeEnum,
} from "@/types/dept-tutorials";
import { localSet } from "@/utils/storage";

import { IFileData, IFormInstance, IGenerateTutorialProps } from "./types";
export const useStore = (props: IGenerateTutorialProps) => {
  const { onClose, defaultData, data, onChange } = props;

  const [modal, contextHolder] = Modal.useModal();

  const { t } = useTranslation();

  const navigate = useNavigate();

  const location = useLocation();

  const [form] = Form.useForm<IFormInstance>();

  const [isShowPodcast, isShowPodcastActions] = useBoolean(false);

  const formIsChanged = useRef<boolean>(false);

  const [loading, setLoading] = useState(false);

  const [previewInfo, setPreviewInfo] = useState<IDeptTutorialDetail>({
    block_hash: "",
    block_height: 0,
    cover: "",
    created_at: "",
    created_by: "",
    description: "",
    end_time: "",
    id: "",
    serial_number: "",
    start_time: "",
    status: 1,
    status_name: "",
    watching_status: 0,
    watching_status_name: 0,
    title: "",
    type: 0,
    type_name: "",
    updated_at: "",
    updated_by: "",
    attachments: [],
    scopes: [],
    timbres: [],
  });

  const [openPreviewModal, openPreviewModalAction] = useBoolean();

  const [previewIndex, setPreviewIndex] = useState<number>();

  const updateLoading = () => {
    setLoading(!loading);
  };

  const postCreateTutorialRequest = useRequest(postCreateTutorialApi, {
    manual: true,
    onSuccess: () => {
      form.resetFields();
      onClose();
      message.open({
        type: "success",
        content: "Success",
      });
    },
    onBefore: updateLoading,
    onFinally: updateLoading,
  });

  const getPodcastPermissionRequest = useRequest(getPodcastPermissionApi, {
    manual: true,
    onSuccess: ({ data }) => {
      data.permission === 1 && isShowPodcastActions.setTrue();
    },
  });

  const putEditTutorialRequest = useRequest(putEditTutorialApi, {
    manual: true,
    onSuccess: (res, params) => {
      form.resetFields();
      onClose();
      onChange?.(params);
      message.open({
        type: "success",
        content: "Success",
      });
    },
    onBefore: updateLoading,
    onFinally: updateLoading,
  });

  const handleCreateTutorial = (data: ICreateTutorialRequest) => {
    postCreateTutorialRequest.run(data);
  };

  const handleEditTutorial = (id: string, data: IEditTutorialRequest) => {
    putEditTutorialRequest.run(id, data);
  };

  const handleIntelligentCreation = () => {
    if (!isShowPodcast) {
      return;
    }
    const values = form.getFieldsValue();

    localSet(
      "isIntelligentCreation",
      JSON.stringify({
        ...omit(["personnelScope"], values),
        personnelScope: isNil(values.personnelScope)
          ? null
          : map((item) => item.key, values.personnelScope),
      })
    );
    isNil(defaultData) || localSet("tutorialEditId", defaultData.id);
    onClose();
    navigate("/intelligent-creation/ai-podcast", {
      state: {
        from: location.pathname,
      },
    });
  };

  const handleOk = () => {
    form.validateFields().then((value) => {
      const { title, description, shelfTime, personnelScope, courseware } =
        value;

      const [start_time, end_time] = shelfTime.map((time: Moment) => {
        return time.toJSON();
      });

      const scopes = personnelScope.map((item) => item.key);

      const attachmentPaths = courseware
        .filter((item) => !!item.path)
        .map((item) => item.path);

      if (defaultData?.id) {
        handleEditTutorial(defaultData.id, {
          attachments: attachmentPaths,
          description,
          end_time,
          start_time,
          title,
          scopes,
        });
      } else {
        handleCreateTutorial({
          attachments: attachmentPaths,
          description,
          end_time,
          start_time,
          title,
          scopes,
          podcast_id:
            courseware.length === 1 && !isNil(courseware[0].podcastId)
              ? courseware[0].podcastId
              : undefined,
        });
      }
    });
  };

  const handleCancel = () => {
    formIsChanged.current
      ? modal.confirm({
          title: t("cancel_making"),
          content: t("whether_to_give_up_save"),
          centered: true,
          okText: t("yes"),
          okButtonProps: {
            style: {
              background: "#475aec",
              borderRadius: "40px",
              fontSize: "14px",
              fontWeight: "400",
            },
            color: "white",
          },
          cancelText: t("no"),
          cancelButtonProps: {
            style: {
              border: "1px solid #475aec",
              borderRadius: "40px",
              fontSize: "14px",
              fontWeight: "400",
              color: "#475aec",
            },
            color: "#475aec",
          },
          onOk() {
            form.resetFields();
            onClose();
          },
        })
      : onClose();
  };

  const handlePreview = (files?: IFileData[], index?: number) => {
    if (files) {
      const fileType =
        files[0].type === "application/pdf"
          ? TutorialTypeEnum.Document
          : files[0].type === "video/mp4"
          ? TutorialTypeEnum.Video
          : files[0].type?.includes("image")
          ? TutorialTypeEnum.Image
          : TutorialTypeEnum.Audio;

      fileType === TutorialTypeEnum.Image && setPreviewIndex(index);
      previewInfo &&
        setPreviewInfo({
          ...previewInfo,
          timbres: [],
          type: fileType,
          attachments: map((file) => {
            return {
              duration: 0,
              filename: file.name,
              id: file.uid,
              mime_type: file.type,
              path: file.path,
              url: file.url,
            };
          }, files),
        });
    }
    openPreviewModalAction.setTrue();
  };

  useUpdateEffect(() => {
    data && setPreviewInfo(data);
  }, [data]);

  useMount(() => {
    getPodcastPermissionRequest.run();
  });

  return {
    t,
    contextHolder,
    form,
    loading,
    previewInfo,
    previewIndex,
    openPreviewModal,
    openPreviewModalAction,
    formIsChanged,
    isShowPodcast,
    setPreviewIndex,
    setPreviewInfo,
    handleOk,
    handleCancel,
    handlePreview,
    handleIntelligentCreation,
  };
};
